import { CloseOutline } from '@olist/design-system-icons';
import styled, { keyframes, css } from 'styled-components';
import '../Text/Caption/Caption.js';
import DetailedSubheading from '../Text/DetailedSubheading/DetailedSubheading.js';
import '../Text/OverlineLg/OverlineLg.js';
import OverlineMd from '../Text/OverlineMd/OverlineMd.js';
import '../Text/ParagraphLg/ParagraphLg.js';
import '../Text/ParagraphMd/ParagraphMd.js';
import '../Text/SectionHeading/SectionHeading.js';
import '../Text/SectionSubheading/SectionSubheading.js';
import '../Text/Title/Title.js';

const fadeIn = keyframes `
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;
const fadeOut = keyframes `
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;
const Container = styled.div `
  cursor: pointer;
  display: inline-block;
  position: relative;
`;
const Overline = styled(OverlineMd)(({ theme: { olist: theme } }) => css `
    color: ${theme.color.neutral.base};
  `);
const Title = styled(DetailedSubheading)(({ theme: { olist: theme } }) => css `
    line-height: 28px;
    padding-bottom: ${theme.spacing.stack['1xsmall']};
  `);
const Content = styled.p(({ theme: { olist: theme } }) => css `
    color: ${theme.color.neutral.base};
    font-size: ${theme.fontSize['2xsmall']};
    line-height: ${theme.lineHeight.small};
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.stack.small};
    align-self: stretch;

    img,
    svg {
      width: 100%;
      align-self: stretch;
      border-radius: ${theme.borderRadius.small};
      border: 1px solid ${theme.color.neutral.soft};
    }
  `);
const CloseIcon = styled(CloseOutline)(({ theme: { olist: theme } }) => css `
    display: flex;
    min-width: 24px;
    min-height: 24px;
    justify-content: center;
    align-items: center;
    color: ${theme.color.neutral.base};

    &:hover {
      color: ${theme.color.neutral.strong};
    }
  `);
const Wrapper = styled.div(({ isOpen, placement, variation = 'neutral', theme: { olist: theme }, width, w, height, h, minWidth, minHeight, maxWidth, maxHeight, }) => {
    var _a, _b;
    return css `
    display: ${isOpen ? 'flex' : 'none'};
    padding: ${theme.spacing.stack.small};
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    box-sizing: border-box;

    opacity: 0;
    position: absolute;
    user-select: text;
    white-space: normal;
    z-index: 1;

    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.components.popover.variation[variation].color};
    background: ${theme.color.neutral.softest};
    box-shadow: 0px 8px 16px 0px rgba(11, 19, 74, 0.04),
      0px 4px 8px 0px rgba(11, 19, 74, 0.06),
      0px 2px 4px 0px rgba(11, 19, 74, 0.08);

    animation: ${isOpen ? fadeIn : fadeOut} 0.3s
      cubic-bezier(0.39, 0.575, 0.565, 1) both;

    min-height: ${minHeight};
    min-width: ${minWidth !== null && minWidth !== void 0 ? minWidth : '160px'};
    max-height: ${maxHeight};
    max-width: ${maxWidth};
    width: ${(_a = w !== null && w !== void 0 ? w : width) !== null && _a !== void 0 ? _a : '100%'};
    height: ${(_b = h !== null && h !== void 0 ? h : height) !== null && _b !== void 0 ? _b : 'auto'};

    &:after,
    &:before {
      border: solid transparent;
      content: '';
      pointer-events: none;
      position: absolute;
      width: 0;
    }

    /* arrow horizontal center */
    ${['bottom', 'bottomLeft', 'bottomRight', 'top', 'topLeft'].includes(placement) &&
        css `
      &:after,
      &:before {
        transform: translateX(-50%);
        left: 50%;
      }
    `}

    ${['bottomRight', 'topRight'].includes(placement) &&
        css `
      &:after,
      &:before {
        transform: translateX(-50%);
        left: 50%;
        margin-right: -17px;
      }
    `}

  /* arrow vertical center */
  ${[
        'left',
        'leftTop',
        'leftBottom',
        'right',
        'rightTop',
        'rightBottom',
    ].includes(placement) &&
        css `
      &:after,
      &:before {
        margin-top: -10px;
        top: 50%;
      }
    `}

  /* position arrow in left */
  ${['bottomLeft', 'topLeft'].includes(placement) &&
        css `
      left: 0;
      &:after,
      &:before {
        margin-left: 0;
        left: 16px;
      }
      &:before {
        left: 16px;
      }
    `}

  /* position arrow in right */
  ${['bottomRight', 'topRight'].includes(placement) &&
        css `
      right: 0;
      &:after,
      &:before {
        left: auto;
        right: 16px;
      }
      &:before {
        right: 14px;
      }
    `}

  /* position arrow in top */
  ${['leftTop', 'rightTop'].includes(placement) &&
        css `
      top: 0;
      &:after,
      &:before {
        margin-left: 0;
        top: 17px;
      }
      &:before {
        top: 16px;
      }
    `}

  /* position arrow in bottom*/
  ${['leftBottom', 'rightBottom'].includes(placement) &&
        css `
      bottom: 0;
      &:after,
      &:before {
        bottom: 17px;
        margin-left: 0;
        top: auto;
      }
      &:before {
        bottom: 16px;
      }
    `}

  /* bottom popover */
  ${['bottom', 'bottomLeft', 'bottomRight'].includes(placement) &&
        css `
      bottom: 0;
      transform: translateY(calc(100% + 10px));

      &:after,
      &:before {
        border-bottom-color: ${theme.color.neutral.softest};
        border-width: 9px;
        top: -17px;
      }

      &:before {
        border-bottom-color: ${theme.components.popover.variation[variation]
            .color};
        border-width: 10px;
        top: -20px;
      }
    `}

  /* left popover */
  ${['left', 'leftBottom', 'leftTop'].includes(placement) &&
        css `
      left: 0;
      transform: translateX(calc(-100% - 10px));

      &:after,
      &:before {
        border-left-color: ${theme.color.neutral.softest};
        border-width: 9px;
        right: -17px;
      }

      &:before {
        border-left-color: ${theme.components.popover.variation[variation]
            .color};
        border-width: 10px;
        right: -20px;
      }
    `}

  /* right popover */
  ${['right', 'rightBottom', 'rightTop'].includes(placement) &&
        css `
      transform: translateX(calc(100% + 10px));
      right: 0;

      &:after,
      &:before {
        border-right-color: ${theme.color.neutral.softest};
        border-width: 9px;
        left: -17px;
      }

      &:before {
        border-right-color: ${theme.components.popover.variation[variation]
            .color};
        border-width: 10px;
        left: -20px;
      }
    `}

  /* top popover */
  ${['top', 'topLeft', 'topRight'].includes(placement) &&
        css `
      top: 0;
      transform: translateY(calc(-100% - 10px));

      &:after {
        border-top-color: ${theme.color.neutral.softest};
        border-width: 9px;
        bottom: -17px;
      }

      &:before {
        border-top-color: ${theme.components.popover.variation[variation]
            .color};
        border-width: 10px;
        bottom: -20px;
      }
    `}

  /* center bottom and top popover */
  ${['bottom', 'top'].includes(placement) &&
        css `
      left: 50%;
    `}

  ${['bottom'].includes(placement) &&
        css `
      transform: translate(-50%, calc(100% + 10px));
    `}

  ${['top'].includes(placement) &&
        css `
      transform: translate(-50%, calc(-100% - 10px));
    `}

  /* center left and right popover */
  ${['left', 'right'].includes(placement) &&
        css `
      top: 50%;
    `}

  ${['left'].includes(placement) &&
        css `
      transform: translate(calc(-100% - 10px), -50%);
    `}

  ${['right'].includes(placement) &&
        css `
      transform: translate(calc(100% + 10px), -50%);
    `}
  `;
});

export { CloseIcon, Container, Content, Overline, Title, Wrapper };
